import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLiveSpaceStateSafe } from '@components/Live/LiveClientProvider'
import { MINUTE_MS } from 'data/constants'
import { useUserPresenceQuery } from 'hooks/useUserPresenceQuery'
import { useSpaceId, useThrottledTimelineStore } from 'use-towns-client'

const GREEN_TIME = MINUTE_MS * 15

const getGreen = (ms: number) => Date.now() - ms < GREEN_TIME

/**
 * Hook to determine if a user should show the green "online" dot.
 *
 * Uses a hybrid approach:
 * 1. Live presence (subscribers list) - definitively online if in first 32 subscribers
 * 2. Activity detection - if user had recent event, query presence API to confirm
 * 3. Presence API query - cached check for users showing activity but not in first 32
 *
 * This handles the pagination limit (32 users) in live presence by querying
 * the presence API for users that show activity but aren't in the first page.
 */
export const useGreenDot = (userId: string | undefined): boolean => {
    const spaceId = useSpaceId()
    const liveSpaceState = useLiveSpaceStateSafe(spaceId)

    // Check if user is in live presence subscribers list
    const isInLivePresence = useMemo(() => {
        if (!userId || !liveSpaceState) {
            return false
        }
        return liveSpaceState.subscribers.userIds.includes(userId)
    }, [userId, liveSpaceState])

    // Check if we have the complete subscriber list (no pagination needed)
    // If totalCount <= userIds.length, we have everyone - no need to query API
    const hasCompleteSubscriberList = useMemo(() => {
        if (!liveSpaceState) {
            return false
        }
        return liveSpaceState.subscribers.totalCount <= liveSpaceState.subscribers.userIds.length
    }, [liveSpaceState])

    // Activity-based detection (old system)
    const lastEventMs = useThrottledTimelineStore(
        useCallback(
            (state) => {
                const lastEventMs = userId
                    ? (state.lastestEventByUser[userId]?.createdAtEpochMs ?? 0)
                    : 0
                return getGreen(lastEventMs) ? lastEventMs : 0
            },
            [userId],
        ),
    )

    const [hasRecentActivity, setHasRecentActivity] = useState(getGreen(lastEventMs))

    useEffect(() => {
        const isGreen = getGreen(lastEventMs)
        setHasRecentActivity(isGreen)
        if (isGreen) {
            const timeout = setTimeout(
                () => {
                    setHasRecentActivity(getGreen(lastEventMs))
                },
                GREEN_TIME - (Date.now() - lastEventMs),
            )
            return () => {
                clearTimeout(timeout)
            }
        }
    }, [lastEventMs])

    // Only query presence API if:
    // - User has recent activity (potential green)
    // - User is NOT already in the subscribers list
    // - We DON'T have the complete subscriber list (need to check server)
    const shouldQueryPresence = hasRecentActivity && !isInLivePresence && !hasCompleteSubscriberList
    const { isOnline: presenceQueryResult } = useUserPresenceQuery(
        shouldQueryPresence ? spaceId : undefined,
        shouldQueryPresence ? userId : undefined,
    )

    // Green if in live presence list
    if (isInLivePresence) {
        return true
    }

    // If we have the complete list and user is not in it, they're definitely offline
    if (hasCompleteSubscriberList) {
        return false
    }

    // For paginated lists, check activity + presence query
    if (hasRecentActivity) {
        // If we queried and got a result, use it
        // If query is pending (undefined), show green optimistically
        return presenceQueryResult ?? true
    }

    return false
}
